import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { MapPin, Users, DollarSign, Settings, Plus } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface VenueSection {
  id: string;
  venue_id: string;
  field_id?: string;
  section_name: string;
  section_type: string;
  capacity?: number;
  hourly_rate?: number;
  amenities?: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface VenueField {
  id: string;
  field_name: string;
  field_type: string;
}

interface VenueSectionsDisplayProps {
  venueId: string;
  showAddButton?: boolean;
  onAddSection?: () => void;
}

const VenueSectionsDisplay: React.FC<VenueSectionsDisplayProps> = ({
  venueId,
  showAddButton = false,
  onAddSection
}) => {
  const [sections, setSections] = useState<VenueSection[]>([]);
  const [fields, setFields] = useState<VenueField[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSections();
    loadFields();
  }, [venueId]);

  const loadSections = async () => {
    try {
      const { data, error } = await supabase
        .from('venue_sections')
        .select('*')
        .eq('venue_id', venueId)
        .eq('is_active', true)
        .order('section_name');

      if (error) throw error;
      setSections((data || []).map((section: any) => ({
        ...section,
        amenities: Array.isArray(section.amenities) ? section.amenities.map((item: any) => 
          typeof item === 'string' ? item : String(item)
        ) : []
      })));
    } catch (error) {
      console.error('Error loading sections:', error);
      toast.error('Failed to load venue sections');
    } finally {
      setLoading(false);
    }
  };

  const loadFields = async () => {
    try {
      const { data, error } = await supabase
        .from('venue_fields')
        .select('*')
        .eq('venue_id', venueId)
        .eq('is_active', true);

      if (error) throw error;
      setFields((data || []).map(field => ({
        ...field,
        field_type: field.sport_type || 'general'
      })));
    } catch (error) {
      console.error('Error loading fields:', error);
    }
  };

  const getFieldName = (fieldId?: string) => {
    if (!fieldId) return 'General';
    const field = fields.find(f => f.id === fieldId);
    return field?.field_name || 'Unknown Field';
  };

  if (loading) {
    return (
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="p-6">
          <div className="text-center text-gray-400">Loading sections...</div>
        </CardContent>
      </Card>
    );
  }

  if (sections.length === 0) {
    return (
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-white">Venue Sections</CardTitle>
          {showAddButton && (
            <Button
              onClick={onAddSection}
              variant="outline"
              size="sm"
              className="text-green-400 border-green-400 hover:bg-green-400 hover:text-black"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Section
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="text-center text-gray-400">
            <MapPin className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No sections configured yet</p>
            {showAddButton && (
              <p className="text-sm mt-1">Add sections to organize your venue space</p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gray-800 border-gray-700">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-white">Venue Sections ({sections.length})</CardTitle>
        {showAddButton && (
          <Button
            onClick={onAddSection}
            variant="outline"
            size="sm"
            className="text-green-400 border-green-400 hover:bg-green-400 hover:text-black"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Section
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sections.map((section) => (
            <div
              key={section.id}
              className="bg-gray-700 rounded-lg p-4 border border-gray-600 hover:border-gray-500 transition-colors"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-white font-medium text-lg">{section.section_name}</h3>
                  <p className="text-gray-400 text-sm">{getFieldName(section.field_id)}</p>
                </div>
                <Badge 
                  variant="secondary" 
                  className="bg-blue-600 text-white text-xs"
                >
                  {section.section_type}
                </Badge>
              </div>

              <div className="space-y-2">
                {section.capacity && (
                  <div className="flex items-center text-gray-300 text-sm">
                    <Users className="h-4 w-4 mr-2" />
                    <span>Capacity: {section.capacity}</span>
                  </div>
                )}

                {section.hourly_rate && (
                  <div className="flex items-center text-gray-300 text-sm">
                    <DollarSign className="h-4 w-4 mr-2" />
                    <span>${section.hourly_rate}/hour</span>
                  </div>
                )}

                {section.amenities && section.amenities.length > 0 && (
                  <div className="mt-3">
                    <p className="text-gray-400 text-xs mb-1">Amenities:</p>
                    <div className="flex flex-wrap gap-1">
                      {section.amenities.slice(0, 3).map((amenity, index) => (
                        <Badge
                          key={index}
                          variant="outline"
                          className="text-xs text-gray-300 border-gray-500"
                        >
                          {amenity}
                        </Badge>
                      ))}
                      {section.amenities.length > 3 && (
                        <Badge
                          variant="outline"
                          className="text-xs text-gray-300 border-gray-500"
                        >
                          +{section.amenities.length - 3} more
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {showAddButton && (
                <div className="mt-3 pt-3 border-t border-gray-600">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-gray-400 hover:text-white"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Manage
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default VenueSectionsDisplay;